// Retry policy (API-40). Idempotent GETs retry (2×, jittered backoff); unsafe
// methods retry ONLY when an Idempotency-Key is present (AP-D7) so a replay can
// never double-book or double-charge. Retries fire on 429 and 5xx (and transport
// errors). `429` honors Retry-After when the server supplies it (API-17).

export interface RetryPolicy {
  retries: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

export const defaultRetryPolicy: RetryPolicy = {
  retries: 2,
  baseDelayMs: 200,
  maxDelayMs: 2000,
};

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export function isRetryableMethod(method: string, hasIdempotencyKey: boolean): boolean {
  if (IDEMPOTENT_METHODS.has(method.toUpperCase())) return true;
  return hasIdempotencyKey;
}

export function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

// Exponential backoff with full jitter, clamped to maxDelayMs. `attempt` is 0-based.
export function backoffDelayMs(attempt: number, policy: RetryPolicy = defaultRetryPolicy): number {
  const exp = Math.min(policy.maxDelayMs, policy.baseDelayMs * 2 ** attempt);
  return Math.round(Math.random() * exp);
}

// Prefer the server's Retry-After (seconds) over computed backoff when present.
export function retryAfterMs(response: Response | undefined): number | undefined {
  const header = response?.headers.get('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  return Number.isFinite(seconds) ? seconds * 1000 : undefined;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
